import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Navigation, Phone, Store } from "lucide-react";
import { shopMapsLink } from "../lib/site";

const Row = ({ label, value, testid }) => (
  <div className="grid grid-cols-[96px_1fr] gap-3 border-b border-navy/5 py-2 text-sm last:border-0">
    <dt className="font-mono text-[11px] uppercase tracking-widest text-slate-500 pt-0.5">{label}</dt>
    <dd data-testid={testid} className={value ? "font-medium text-navy" : "italic text-slate-400"}>{value || "To be updated"}</dd>
  </div>
);

export default function ShopCard({ s, index = 0 }) {
  return (
    <motion.article
      data-testid={`shop-card-${s.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group flex flex-col overflow-hidden rounded-[22px] border border-navy/10 bg-white transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(11,25,44,0.45)]"
    >
      <div className="relative aspect-square overflow-hidden bg-navy">
        {s.photo ? (
          <img src={s.photo} alt={`${s.name} shop`} loading="lazy" className="h-full w-full object-cover object-bottom transition-transform duration-[900ms] group-hover:scale-[1.05]" />
        ) : (
          <div className="grid h-full place-items-center text-slate-300"><span className="flex flex-col items-center gap-2 text-sm"><Store className="h-8 w-8 text-brand" />Shop photo to be added</span></div>
        )}
        {s.branch && (
          <span data-testid={`shop-badge-${s.id}`} className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-navy/90 px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-widest text-white shadow-lg ring-1 ring-white/20 backdrop-blur"><span className="h-1.5 w-1.5 rounded-full bg-brand" />{s.branch}</span>
        )}
        <span className="absolute bottom-0 left-0 h-1 w-full bg-brand" />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 data-testid={`shop-name-${s.id}`} className="font-display text-2xl font-bold uppercase leading-tight text-navy">{s.name}</h3>
        <dl className="mt-4">
          <Row label="Address" value={s.address} testid={`shop-address-${s.id}`} />
          {s.maps_url && (
            <div className="pb-3 pt-1">
              <a
                href={s.maps_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`shop-view-map-${s.id}`}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-4 py-3 text-sm font-semibold text-white shadow-[0_10px_30px_-10px_rgba(255,107,0,0.7)] transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-brand-dark"
              >
                <MapPin className="h-4 w-4" /> View on Google Maps
              </a>
            </div>
          )}
          <Row label="Area / City" value={s.area} testid={`shop-area-${s.id}`} />
          <Row label="District" value={s.district ? [s.district, s.state].filter(Boolean).join(", ") : ""} testid={`shop-district-${s.id}`} />
          <Row label="PIN code" value={s.pincode} testid={`shop-pincode-${s.id}`} />
          <div className="grid grid-cols-[96px_1fr] gap-3 py-2 text-sm">
            <dt className="font-mono text-[11px] uppercase tracking-widest text-slate-500 pt-0.5">Contact</dt>
            <dd>
              {s.phone ? (
                <span className="flex flex-col gap-1">
                  {s.phone.split(/[/,]/).map((n) => n.trim()).filter(Boolean).map((n) => (
                    <a key={n} href={`tel:+91${n.replace(/\D/g, "").slice(-10)}`} data-testid={`shop-phone-${s.id}-${n.replace(/\D/g, "")}`} className="inline-flex items-center gap-1.5 font-semibold text-navy hover:text-brand">
                      <Phone className="h-4 w-4 text-brand" /> {n}
                    </a>
                  ))}
                </span>
              ) : <span className="italic text-slate-400">To be updated</span>}
            </dd>
          </div>
        </dl>
        <div className="mt-auto pt-5">
          <a
            href={shopMapsLink(s)}
            target="_blank"
            rel="noreferrer"
            data-testid={`shop-directions-${s.id}`}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand"
          >
            <Navigation className="h-4 w-4" /> {typeof s.lat === "number" ? "Get Directions" : "Open in Google Maps"}
          </a>
        </div>
      </div>
    </motion.article>
  );
}

export const ShopsEmpty = () => (
  <div data-testid="shops-empty" className="rounded-[22px] border border-dashed border-navy/20 bg-white p-10 text-center">
    <Store className="mx-auto h-10 w-10 text-brand" />
    <p className="mt-4 font-display text-2xl font-bold uppercase text-navy">Shop details coming soon</p>
    <p className="mt-2 text-slate-600">Our shop locations are being added. <Link to="/contact" className="font-semibold text-brand">Contact us</Link> for the nearest outlet.</p>
  </div>
);
